/**
 * Uploads the addon button's screenshots from the flavour's `Screenshots` folder. It asks the
 * site which stems are still waiting (`GET /api/addon/shots`, filled from uploaded Thottbot.lua
 * files), lists the folder's names, and reads only a file whose name matches a waiting stem:
 * no other screenshot is ever opened. Two files within the match window of one stem are
 * ambiguous (Print Screen pressed with the button), so neither is read. One upload at a time,
 * spaced to fit the server's per-hour screenshot limit; kept out of React for fake timers.
 */
import { matchesStem, type AddonShotUploadResponse } from '@thottbot/shared';
import type { PendingOutcome, ShotOutcome } from '../api/addon-shots';
import type { ScreenshotDir } from './discover';
import type { FileEntry } from './types';

export const SHOT_CHECK_MS = 30_000;
/** 6 min between uploads: 10 an hour, the server's screenshot limit (shared with the gallery button). */
export const SHOT_SPACING_MS = 360_000;
export const SHOT_RETRY_MS = 60_000;

export interface ShotTiming {
  checkMs: number;
  spacingMs: number;
}

export type ShotState = 'watching' | 'paused' | 'signed_out';
export type ShotLogKind =
  'uploaded' | 'duplicate' | 'skipped' | 'dropped' | 'rejected' | 'rate_limited' | 'error' | 'unauthorized';

export interface ShotLogEntry {
  at: number;
  kind: ShotLogKind;
  stem: string;
  message: string;
  artifact?: AddonShotUploadResponse['artifact'];
}

export interface ShotSnapshot {
  state: ShotState;
  /** Waiting stems with no matching file yet (the game has not written it, or it is elsewhere). */
  waiting: number;
  uploading: string | null;
  log: ShotLogEntry[];
}

export interface ShotUploaderDeps {
  dirs: ScreenshotDir[];
  listPending: () => Promise<PendingOutcome>;
  upload: (blob: Blob, stem: string, fileName: string) => Promise<ShotOutcome>;
  timing?: Partial<ShotTiming>;
  now?: () => number;
}

export interface ShotUploader {
  start(): void;
  stop(): void;
  pause(): void;
  resume(): void;
  sessionRestored(): void;
  /** Look now (the page calls it after a Thottbot.lua upload, which may add stems). */
  checkNow(): void;
  subscribe(listener: () => void): () => void;
  snapshot(): ShotSnapshot;
}

const MAX_LOG = 50;

export function createShotUploader(deps: ShotUploaderDeps): ShotUploader {
  const timing: ShotTiming = { checkMs: SHOT_CHECK_MS, spacingMs: SHOT_SPACING_MS, ...deps.timing };
  const now = deps.now ?? (() => Date.now());
  let state: ShotState = 'watching';
  let stopped = true;
  let running = false;
  let again = false;
  let waiting = 0;
  let uploading: string | null = null;
  let nextUploadAt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  /** Stems the site will not take, and ambiguous stems already reported. */
  const dropped = new Set<string>();
  const ambiguous = new Set<string>();
  const log: ShotLogEntry[] = [];
  const listeners = new Set<() => void>();
  let snap: ShotSnapshot | null = null;

  function emit(): void {
    snap = null;
    for (const l of listeners) l();
  }
  function addLog(entry: Omit<ShotLogEntry, 'at'>): void {
    log.unshift({ at: now(), ...entry });
    if (log.length > MAX_LOG) log.length = MAX_LOG;
  }
  function schedule(ms: number): void {
    if (stopped) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      void cycle();
    }, Math.max(0, ms));
  }

  /** Names only: listing a folder opens no file. */
  async function listFiles(): Promise<{ name: string; handle: FileEntry }[]> {
    const out: { name: string; handle: FileEntry }[] = [];
    for (const d of deps.dirs) {
      for await (const entry of d.dir.values()) if (entry.kind === 'file') out.push({ name: entry.name, handle: entry });
    }
    return out;
  }

  async function cycle(): Promise<void> {
    if (stopped) return;
    if (running) {
      again = true;
      return;
    }
    running = true;
    let wait = timing.checkMs;
    try {
      if (state !== 'watching') return;
      const pending = await deps.listPending();
      if (pending.kind === 'unauthorized') {
        state = 'signed_out';
        return;
      }
      if (pending.kind === 'error') return;
      const files = await listFiles();
      let ready: { stem: string; name: string; handle: FileEntry } | null = null;
      waiting = 0;
      for (const { stem } of pending.pending) {
        if (dropped.has(stem)) continue;
        const matches = files.filter((f) => matchesStem(f.name, stem));
        if (matches.length === 0) waiting += 1;
        else if (matches.length > 1) {
          if (!ambiguous.has(stem)) {
            ambiguous.add(stem);
            addLog({ kind: 'skipped', stem, message: 'Skipped: more than one screenshot within 2 seconds of this one' });
          }
        } else ready ??= { stem, ...matches[0]! };
      }
      if (!ready) return;
      if (now() < nextUploadAt) {
        wait = Math.min(wait, nextUploadAt - now());
        return;
      }
      wait = await send(ready);
    } finally {
      running = false;
      emit();
      if (again) {
        again = false;
        schedule(0);
      } else if (state === 'watching') schedule(wait);
    }
  }

  /** Upload one file; returns how long until the next cycle. */
  async function send(item: { stem: string; name: string; handle: FileEntry }): Promise<number> {
    uploading = item.stem;
    emit();
    let outcome: ShotOutcome;
    try {
      const blob = await item.handle.getFile();
      outcome = await deps.upload(blob, item.stem, item.name);
    } catch (err) {
      outcome = { kind: 'error', message: (err as Error).message };
    } finally {
      uploading = null;
    }
    const { stem } = item;
    switch (outcome.kind) {
      case 'uploaded':
        nextUploadAt = now() + timing.spacingMs;
        addLog({
          kind: outcome.result.duplicate ? 'duplicate' : 'uploaded',
          stem,
          message: outcome.result.duplicate ? 'Already uploaded' : 'Uploaded',
          artifact: outcome.result.artifact,
        });
        return 0;
      case 'dropped':
        dropped.add(stem);
        addLog({
          kind: 'dropped',
          stem,
          message:
            outcome.code === 'no_page'
              ? 'Not uploaded: no page to attach it to (instances and unknown NPCs have none)'
              : `Not uploaded: ${outcome.message}`,
        });
        return 0;
      case 'rejected':
        dropped.add(stem);
        nextUploadAt = now() + timing.spacingMs;
        addLog({ kind: 'rejected', stem, message: outcome.message });
        return 0;
      case 'rate_limited':
        nextUploadAt = now() + (outcome.retryAfterMs ?? timing.spacingMs);
        addLog({ kind: 'rate_limited', stem, message: 'The site is rate-limiting screenshots; retrying later' });
        return nextUploadAt - now();
      case 'retry':
        return timing.checkMs;
      case 'unauthorized':
        state = 'signed_out';
        addLog({ kind: 'unauthorized', stem, message: 'Your session has expired; sign in again to continue' });
        return timing.checkMs;
      case 'error':
        nextUploadAt = now() + SHOT_RETRY_MS;
        addLog({ kind: 'error', stem, message: `${outcome.message}; retrying in ${SHOT_RETRY_MS / 1000} s` });
        return SHOT_RETRY_MS;
    }
  }

  return {
    start() {
      if (!stopped) return;
      stopped = false;
      schedule(0);
    },
    stop() {
      stopped = true;
      if (timer) clearTimeout(timer);
      timer = null;
    },
    pause() {
      if (state === 'signed_out') return;
      state = 'paused';
      emit();
    },
    resume() {
      if (state !== 'paused') return;
      state = 'watching';
      emit();
      schedule(0);
    },
    sessionRestored() {
      if (state !== 'signed_out') return;
      state = 'watching';
      emit();
      schedule(0);
    },
    checkNow() {
      if (state === 'watching') schedule(0);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    snapshot() {
      snap ??= { state, waiting, uploading, log: [...log] };
      return snap;
    },
  };
}
